import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { API_BASE } from '../../config';
import { useLocale } from '../../contexts/LocaleContext';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { AdminAccessDenied } from './AdminAccessDenied';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import {
  ADMIN_BADGE_INFO_CLASSNAME,
  ADMIN_BADGE_WARNING_CLASSNAME,
  ADMIN_BODY_TEXT_CLASSNAME,
  ADMIN_CHART_TOOLTIP_STYLE,
  ADMIN_CHARTS_GRID_CLASSNAME,
  ADMIN_EMPTY_BOX_CLASSNAME,
  ADMIN_FIELD_LABEL_CLASSNAME,
  ADMIN_INLINE_LINK_BUTTON_CLASSNAME,
  ADMIN_INLINE_SELECT_CLASSNAME,
  ADMIN_MODAL_CLASSNAME,
  ADMIN_MODAL_OVERLAY_CLASSNAME,
  ADMIN_MUTED_TEXT_CLASSNAME,
  ADMIN_PAGE_CLASSNAME,
  ADMIN_PANEL_CLASSNAME,
  ADMIN_PANEL_TITLE_CLASSNAME,
  AdminErrorAlert,
  AdminLoadingState,
  AdminPaginationBar,
  AdminTableWrap,
} from './admin-ui';
import {
  COLOR_FEATURE_EXPORT_PDF_BG,
  COLOR_FEATURE_EXPORT_PDF_TEXT,
  COLOR_FEATURE_EXPORT_PDF_CHART,
  COLOR_FEATURE_EXPORT_WORD_BG,
  COLOR_FEATURE_EXPORT_WORD_TEXT,
  COLOR_FEATURE_EXPORT_WORD_CHART,
  COLOR_FEATURE_IMPORT_BG,
  COLOR_FEATURE_IMPORT_TEXT,
  COLOR_FEATURE_IMPORT_CHART,
  COLOR_FEATURE_CHARACTERS_ANALYSIS_BG,
  COLOR_FEATURE_CHARACTERS_ANALYSIS_TEXT,
  COLOR_FEATURE_CHARACTERS_ANALYSIS_CHART,
  COLOR_FEATURE_CHAPTER_ANALYSIS_BG,
  COLOR_FEATURE_CHAPTER_ANALYSIS_TEXT,
  COLOR_FEATURE_CHAPTER_ANALYSIS_CHART,
  COLOR_FEATURE_SYNONYMS_BG,
  COLOR_FEATURE_SYNONYMS_TEXT,
  COLOR_FEATURE_SYNONYM_SELECTED_BG,
  COLOR_FEATURE_SYNONYM_SELECTED_TEXT,
  COLOR_FEATURE_DOCUMENT_CHAT_BG,
  COLOR_FEATURE_DOCUMENT_CHAT_TEXT,
  COLOR_FEATURE_DOCUMENT_CHAT_CHART,
  COLOR_FEATURE_DOCUMENT_SUGGESTIONS_BG,
  COLOR_FEATURE_DOCUMENT_SUGGESTIONS_TEXT,
  COLOR_FEATURE_DOCUMENT_SUGGESTIONS_CHART,
  COLOR_FEATURE_COMMENT_BG,
  COLOR_FEATURE_COMMENT_TEXT,
  COLOR_FEATURE_COMMENT_CHART,
  COLOR_FEATURE_LOCATIONS_ANALYSIS_BG,
  COLOR_FEATURE_LOCATIONS_ANALYSIS_TEXT,
  COLOR_FEATURE_LOCATIONS_ANALYSIS_CHART,
  COLOR_FEATURE_LOCATION_ANALYSIS_BG,
  COLOR_FEATURE_LOCATION_ANALYSIS_TEXT,
  COLOR_FEATURE_LOCATION_ANALYSIS_CHART,
  COLOR_FEATURE_TECHNICAL_SHEET_REGENERATION_BG,
  COLOR_FEATURE_TECHNICAL_SHEET_REGENERATION_TEXT,
  COLOR_FEATURE_TECHNICAL_SHEET_REGENERATION_CHART,
  COLOR_FEATURE_RESOURCE_ADDITION_BG,
  COLOR_FEATURE_RESOURCE_ADDITION_TEXT,
  COLOR_FEATURE_RESOURCE_ADDITION_CHART,
  COLOR_FEATURE_RESOURCE_ANALYSIS_BG,
  COLOR_FEATURE_RESOURCE_ANALYSIS_TEXT,
  COLOR_FEATURE_RESOURCE_ANALYSIS_CHART,
  COLOR_FEATURE_WEB_SEARCH_BG,
  COLOR_FEATURE_WEB_SEARCH_TEXT,
  COLOR_FEATURE_WEB_SEARCH_CHART,
  COLOR_FEATURE_USER_CREATED_BG,
  COLOR_FEATURE_USER_CREATED_TEXT,
  COLOR_FEATURE_USER_CREATED_CHART,
  COLOR_FEATURE_EXAMPLE_PROJECT_SEEDED_BG,
  COLOR_FEATURE_EXAMPLE_PROJECT_SEEDED_TEXT,
  COLOR_FEATURE_EXAMPLE_PROJECT_SEEDED_CHART,
  COLOR_FEATURE_PLAN_UPGRADED_BG,
  COLOR_FEATURE_PLAN_UPGRADED_TEXT,
  COLOR_FEATURE_PLAN_UPGRADED_CHART,
  COLOR_FEATURE_DEFAULT_BG,
  COLOR_FEATURE_DEFAULT_TEXT,
  COLOR_FEATURE_DEFAULT_CHART,
} from '../../colors';

interface LLMCall {
  id: string;
  userId: string | null;
  documentId: string | null;
  triggerSource: string;
  provider: 'gemini' | 'openai';
  model: string;
  prompt: string;
  response: string | null;
  promptTokens: number | null;
  responseTokens: number | null;
  costUsd: number | null;
  durationSeconds: number | null;
  createdAt: string;
}

interface FeatureTracking {
  id: string;
  userId: string;
  userEmail?: string;
  userAdmin?: boolean;
  documentId: string | null;
  feature: 'export_pdf' | 'export_word' | 'export_docx' | 'import_word' | 'import_pdf' | 'import_epub' | 'characters_analysis' | 'locations_analysis' | 'location_analysis' | 'chapter_analysis' | 'document_analysis' | 'synonyms' | 'synonym_selected' | 'document_chat' | 'document_suggestions' | 'chat_suggestions' | 'comment' | 'technical_sheet_regeneration' | 'resource_addition' | 'resource_analysis' | 'web_search' | 'user_created' | 'example_project_seeded' | 'plan_upgraded' | 'timeline_event' | 'note' | 'project_share' | 'user_document_suggestion' | 'document_project_share' | 'document_read_aloud' | 'character_chat' | 'timeline_auto_extract';
  llmCallId: string | null;
  llmCall?: LLMCall | null;
  createdAt: string;
}

export const FeatureTracking: React.FC = () => {
  const { user, token, masterKey } = useAuth();
  const { t } = useLocale();
  const [featureTracking, setFeatureTracking] = useState<FeatureTracking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFeature, setSelectedFeature] = useState<string>('all');
  const [excludeAdmin, setExcludeAdmin] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [aggregations, setAggregations] = useState<{
    totalCount: number;
    featureCounts: Record<string, number>;
    timelineData?: Array<{ date: string; feature: string; count: number }>;
  } | null>(null);
  const [selectedLLMCall, setSelectedLLMCall] = useState<LLMCall | null>(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [excludeAdmin, selectedFeature]);

  useEffect(() => {
    const fetchFeatureTracking = async () => {
      if ((!token && !masterKey) || !user?.admin) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const params = new URLSearchParams({
          page: currentPage.toString(),
          pageSize: '25',
          excludeAdmin: excludeAdmin.toString(),
          ...(selectedFeature !== 'all' ? { feature: selectedFeature } : {}),
        });
        const url = masterKey
          ? `${API_BASE}/api/feature-tracking?${params.toString()}&masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/feature-tracking?${params.toString()}`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch feature tracking');
        }

        const data = await response.json();
        setFeatureTracking(data.featureTracking || []);
        setTotalPages(data.pagination?.totalPages || 1);
        setTotalCount(data.pagination?.total || 0);
        setAggregations(data.aggregations || null);
        setError(null);
      } catch (err) {
        console.error('Error fetching feature tracking:', err);
        setError(t('failed_to_load_feature_tracking'));
      } finally {
        setLoading(false);
      }
    };

    fetchFeatureTracking();
  }, [token, user, masterKey, t, currentPage, excludeAdmin, selectedFeature]);

  const getFeatureLabel = (feature: string): string => {
    const key = `feature_${feature}`;
    const translated = t(key);
    return translated !== key ? translated : feature;
  };

  const getFeatureColor = (feature: string): { bg: string; text: string } => {
    switch (feature) {
      case 'export_pdf':
        return { bg: COLOR_FEATURE_EXPORT_PDF_BG, text: COLOR_FEATURE_EXPORT_PDF_TEXT };
      case 'export_word':
      case 'export_docx':
        return { bg: COLOR_FEATURE_EXPORT_WORD_BG, text: COLOR_FEATURE_EXPORT_WORD_TEXT };
      case 'import_word':
      case 'import_pdf':
      case 'import_epub':
        return { bg: COLOR_FEATURE_IMPORT_BG, text: COLOR_FEATURE_IMPORT_TEXT };
      case 'characters_analysis':
        return { bg: COLOR_FEATURE_CHARACTERS_ANALYSIS_BG, text: COLOR_FEATURE_CHARACTERS_ANALYSIS_TEXT };
      case 'chapter_analysis':
      case 'document_analysis':
        return { bg: COLOR_FEATURE_CHAPTER_ANALYSIS_BG, text: COLOR_FEATURE_CHAPTER_ANALYSIS_TEXT };
      case 'synonyms':
        return { bg: COLOR_FEATURE_SYNONYMS_BG, text: COLOR_FEATURE_SYNONYMS_TEXT };
      case 'synonym_selected':
        return { bg: COLOR_FEATURE_SYNONYM_SELECTED_BG, text: COLOR_FEATURE_SYNONYM_SELECTED_TEXT };
      case 'document_chat':
        return { bg: COLOR_FEATURE_DOCUMENT_CHAT_BG, text: COLOR_FEATURE_DOCUMENT_CHAT_TEXT };
      case 'document_suggestions':
        return { bg: COLOR_FEATURE_DOCUMENT_SUGGESTIONS_BG, text: COLOR_FEATURE_DOCUMENT_SUGGESTIONS_TEXT };
      case 'chat_suggestions':
        return { bg: COLOR_FEATURE_DOCUMENT_SUGGESTIONS_BG, text: COLOR_FEATURE_DOCUMENT_SUGGESTIONS_TEXT };
      case 'comment':
        return { bg: COLOR_FEATURE_COMMENT_BG, text: COLOR_FEATURE_COMMENT_TEXT };
      case 'user_document_suggestion':
        return { bg: COLOR_FEATURE_DOCUMENT_SUGGESTIONS_BG, text: COLOR_FEATURE_DOCUMENT_SUGGESTIONS_TEXT };
      case 'locations_analysis':
        return { bg: COLOR_FEATURE_LOCATIONS_ANALYSIS_BG, text: COLOR_FEATURE_LOCATIONS_ANALYSIS_TEXT };
      case 'location_analysis':
        return { bg: COLOR_FEATURE_LOCATION_ANALYSIS_BG, text: COLOR_FEATURE_LOCATION_ANALYSIS_TEXT };
      case 'technical_sheet_regeneration':
        return { bg: COLOR_FEATURE_TECHNICAL_SHEET_REGENERATION_BG, text: COLOR_FEATURE_TECHNICAL_SHEET_REGENERATION_TEXT };
      case 'resource_addition':
        return { bg: COLOR_FEATURE_RESOURCE_ADDITION_BG, text: COLOR_FEATURE_RESOURCE_ADDITION_TEXT };
      case 'resource_analysis':
        return { bg: COLOR_FEATURE_RESOURCE_ANALYSIS_BG, text: COLOR_FEATURE_RESOURCE_ANALYSIS_TEXT };
      case 'web_search':
        return { bg: COLOR_FEATURE_WEB_SEARCH_BG, text: COLOR_FEATURE_WEB_SEARCH_TEXT };
      case 'user_created':
        return { bg: COLOR_FEATURE_USER_CREATED_BG, text: COLOR_FEATURE_USER_CREATED_TEXT };
      case 'example_project_seeded':
        return { bg: COLOR_FEATURE_EXAMPLE_PROJECT_SEEDED_BG, text: COLOR_FEATURE_EXAMPLE_PROJECT_SEEDED_TEXT };
      case 'plan_upgraded':
        return { bg: COLOR_FEATURE_PLAN_UPGRADED_BG, text: COLOR_FEATURE_PLAN_UPGRADED_TEXT };
      case 'timeline_event':
      case 'note':
      case 'project_share':
      case 'document_project_share':
      case 'document_read_aloud':
      case 'character_chat':
      case 'timeline_auto_extract':
        return { bg: COLOR_FEATURE_DEFAULT_BG, text: COLOR_FEATURE_DEFAULT_TEXT };
      default:
        return { bg: COLOR_FEATURE_DEFAULT_BG, text: COLOR_FEATURE_DEFAULT_TEXT };
    }
  };

  const getFeatureChartColor = (feature: string): string => {
    switch (feature) {
      case 'export_pdf':
        return COLOR_FEATURE_EXPORT_PDF_CHART;
      case 'export_word':
      case 'export_docx':
        return COLOR_FEATURE_EXPORT_WORD_CHART;
      case 'import_word':
      case 'import_pdf':
      case 'import_epub':
        return COLOR_FEATURE_IMPORT_CHART;
      case 'characters_analysis':
        return COLOR_FEATURE_CHARACTERS_ANALYSIS_CHART;
      case 'chapter_analysis':
      case 'document_analysis':
        return COLOR_FEATURE_CHAPTER_ANALYSIS_CHART;
      case 'document_chat':
        return COLOR_FEATURE_DOCUMENT_CHAT_CHART;
      case 'document_suggestions':
        return COLOR_FEATURE_DOCUMENT_SUGGESTIONS_CHART;
      case 'chat_suggestions':
        return COLOR_FEATURE_DOCUMENT_SUGGESTIONS_CHART;
      case 'comment':
        return COLOR_FEATURE_COMMENT_CHART;
      case 'user_document_suggestion':
        return COLOR_FEATURE_DOCUMENT_SUGGESTIONS_CHART;
      case 'locations_analysis':
        return COLOR_FEATURE_LOCATIONS_ANALYSIS_CHART;
      case 'location_analysis':
        return COLOR_FEATURE_LOCATION_ANALYSIS_CHART;
      case 'technical_sheet_regeneration':
        return COLOR_FEATURE_TECHNICAL_SHEET_REGENERATION_CHART;
      case 'resource_addition':
        return COLOR_FEATURE_RESOURCE_ADDITION_CHART;
      case 'resource_analysis':
        return COLOR_FEATURE_RESOURCE_ANALYSIS_CHART;
      case 'web_search':
        return COLOR_FEATURE_WEB_SEARCH_CHART;
      case 'user_created':
        return COLOR_FEATURE_USER_CREATED_CHART;
      case 'example_project_seeded':
        return COLOR_FEATURE_EXAMPLE_PROJECT_SEEDED_CHART;
      case 'plan_upgraded':
        return COLOR_FEATURE_PLAN_UPGRADED_CHART;
      case 'timeline_event':
      case 'note':
      case 'project_share':
      case 'document_project_share':
      case 'document_read_aloud':
      case 'character_chat':
      case 'timeline_auto_extract':
        return COLOR_FEATURE_DEFAULT_CHART;
      default:
        return COLOR_FEATURE_DEFAULT_CHART;
    }
  };

  const filteredTracking = featureTracking;

  const featureCounts = useMemo(() => {
    return aggregations?.featureCounts || {};
  }, [aggregations]);

  const totalCountValue = aggregations?.totalCount || 0;

  const donutChartData = useMemo(() => {
    return Object.entries(featureCounts).map(([feature, count]) => ({
      name: getFeatureLabel(feature),
      value: count,
      feature,
    }));
  }, [featureCounts]);

  const timelineData = useMemo(() => {
    if (!aggregations?.timelineData || aggregations.timelineData.length === 0) {
      return [];
    }

    const dateMap = new Map<string, Record<string, number>>();
    const allFeatures = new Set<string>();

    aggregations.timelineData.forEach((item) => {
      if (!dateMap.has(item.date)) {
        dateMap.set(item.date, {});
      }
      const dayData = dateMap.get(item.date)!;
      dayData[item.feature] = item.count;
      allFeatures.add(item.feature);
    });

    const sortedDates = Array.from(dateMap.keys()).sort();
    const featuresArray = Array.from(allFeatures);

    return sortedDates.map(date => {
      const dayData = dateMap.get(date)!;
      const result: Record<string, string | number> = { 
        date: new Date(date + 'T00:00:00').toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }) 
      };
      featuresArray.forEach(feature => {
        result[getFeatureLabel(feature)] = dayData[feature] || 0;
      });
      return result;
    });
  }, [aggregations, getFeatureLabel]);

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  if (loading) {
    return <AdminLoadingState />;
  }

  return (
    <>
      <div className={ADMIN_PAGE_CLASSNAME}>
        {error && (
          <div className="mb-6">
            <AdminErrorAlert>{error}</AdminErrorAlert>
          </div>
        )}

        <div className="mb-8 flex flex-wrap items-center gap-4">
          <div className="text-base font-semibold text-foreground">
            {t('total')}: {totalCountValue}
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(featureCounts).map(([feature, count]) => {
              const colors = getFeatureColor(feature);
              return (
                <div
                  key={feature}
                  className="rounded-full px-3 py-0.5 text-sm font-medium"
                  style={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                  }}
                >
                  {getFeatureLabel(feature)}: {count}
                </div>
              );
            })}
          </div>
        </div>

        <div className={ADMIN_CHARTS_GRID_CLASSNAME}>
          <div className={ADMIN_PANEL_CLASSNAME}>
            <h2 className={ADMIN_PANEL_TITLE_CLASSNAME}>
              {t('usage_by_type')}
            </h2>
            {donutChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={donutChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                    outerRadius={80}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {donutChartData.map((entry) => (
                      <Cell key={`cell-${entry.feature}`} fill={getFeatureChartColor(entry.feature)} />
                    ))}
                  </Pie>
                  <Tooltip {...ADMIN_CHART_TOOLTIP_STYLE} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className={ADMIN_EMPTY_BOX_CLASSNAME}>
                {t('no_data')}
              </div>
            )}
          </div>

          <div className={ADMIN_PANEL_CLASSNAME}>
            <h2 className={ADMIN_PANEL_TITLE_CLASSNAME}>
              {t('usage_timeline')}
            </h2>
            {timelineData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={timelineData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" stroke="var(--muted-foreground)" tick={{ fill: 'var(--muted-foreground)' }} />
                  <YAxis stroke="var(--muted-foreground)" tick={{ fill: 'var(--muted-foreground)' }} />
                  <Tooltip {...ADMIN_CHART_TOOLTIP_STYLE} />
                  <Legend />
                  {Object.keys(featureCounts).map((feature) => (
                    <Line
                      key={feature}
                      type="monotone"
                      dataKey={getFeatureLabel(feature)}
                      stroke={getFeatureChartColor(feature)}
                      strokeWidth={2}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className={ADMIN_EMPTY_BOX_CLASSNAME}>
                {t('no_data')}
              </div>
            )}
          </div>
        </div>

        <div className="mb-6 flex flex-wrap items-start gap-8">
          <div className="min-w-0 flex-1">
            <label className={ADMIN_FIELD_LABEL_CLASSNAME}>
              {t('filter_by_feature')}:
            </label>
            <select
              value={selectedFeature}
              onChange={(e) => setSelectedFeature(e.target.value)}
              className={ADMIN_INLINE_SELECT_CLASSNAME}
            >
              <option value="all">{t('all')}</option>
              <option value="export_pdf">{getFeatureLabel('export_pdf')}</option>
              <option value="export_word">{getFeatureLabel('export_word')}</option>
              <option value="export_docx">{getFeatureLabel('export_docx')}</option>
              <option value="import_word">{getFeatureLabel('import_word')}</option>
              <option value="import_pdf">{getFeatureLabel('import_pdf')}</option>
              <option value="import_epub">{getFeatureLabel('import_epub')}</option>
              <option value="characters_analysis">{getFeatureLabel('characters_analysis')}</option>
              <option value="chapter_analysis">{getFeatureLabel('chapter_analysis')}</option>
              <option value="document_analysis">{getFeatureLabel('document_analysis')}</option>
              <option value="document_chat">{getFeatureLabel('document_chat')}</option>
              <option value="document_suggestions">{getFeatureLabel('document_suggestions')}</option>
              <option value="chat_suggestions">{getFeatureLabel('chat_suggestions')}</option>
              <option value="comment">{getFeatureLabel('comment')}</option>
              <option value="user_document_suggestion">{getFeatureLabel('user_document_suggestion')}</option>
              <option value="locations_analysis">{getFeatureLabel('locations_analysis')}</option>
              <option value="location_analysis">{getFeatureLabel('location_analysis')}</option>
              <option value="technical_sheet_regeneration">{getFeatureLabel('technical_sheet_regeneration')}</option>
              <option value="resource_addition">{getFeatureLabel('resource_addition')}</option>
              <option value="resource_analysis">{getFeatureLabel('resource_analysis')}</option>
              <option value="web_search">{getFeatureLabel('web_search')}</option>
              <option value="user_created">{getFeatureLabel('user_created')}</option>
              <option value="example_project_seeded">{getFeatureLabel('example_project_seeded')}</option>
              <option value="plan_upgraded">{getFeatureLabel('plan_upgraded')}</option>
              <option value="timeline_event">{getFeatureLabel('timeline_event')}</option>
              <option value="note">{getFeatureLabel('note')}</option>
              <option value="project_share">{getFeatureLabel('project_share')}</option>
              <option value="document_project_share">{getFeatureLabel('document_project_share')}</option>
              <option value="document_read_aloud">{getFeatureLabel('document_read_aloud')}</option>
              <option value="character_chat">{getFeatureLabel('character_chat')}</option>
              <option value="timeline_auto_extract">{getFeatureLabel('timeline_auto_extract')}</option>
            </select>
          </div>
          <div className="mt-7 flex items-center gap-2">
            <Checkbox
              id="exclude-admin-filter"
              checked={excludeAdmin}
              onCheckedChange={(checked) => setExcludeAdmin(checked === true)}
            />
            <Label htmlFor="exclude-admin-filter" className="cursor-pointer text-sm text-foreground">
              {t('exclude_admin')}
            </Label>
          </div>
        </div>

        <AdminTableWrap>
            <table className="admin-data-table" style={{ minWidth: '800px' }}>
              <thead>
                <tr>
                  <th>
                    {t('user')}
                  </th>
                  <th>
                    {t('feature')}
                  </th>
                  <th>
                    {t('document')}
                  </th>
                  <th>
                    {t('llm_call')}
                  </th>
                  <th>
                    {t('date')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredTracking.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-muted-foreground">
                      {t('no_data')}
                    </td>
                  </tr>
                ) : (
                  filteredTracking.map((ft) => {
                    const colors = getFeatureColor(ft.feature);
                    return (
                      <tr key={ft.id}>
                        <td>
                          {ft.userEmail || ft.userId}
                        </td>
                        <td>
                          <span
                            className="rounded-full px-3 py-0.5 text-xs font-medium"
                            style={{
                              backgroundColor: colors.bg,
                              color: colors.text,
                            }}
                          >
                            {getFeatureLabel(ft.feature)}
                          </span>
                        </td>
                        <td>
                          {ft.documentId ? (
                            <Link
                              to={`/documents/${ft.documentId}`}
                              className="admin-table-link"
                            >
                              {ft.documentId.substring(0, 8)}...
                            </Link>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td>
                          {ft.llmCall ? (
                            <button
                              type="button"
                              onClick={() => setSelectedLLMCall(ft.llmCall!)}
                              className={ADMIN_INLINE_LINK_BUTTON_CLASSNAME}
                            >
                              <div className="flex flex-col gap-1">
                                <span
                                  className={
                                    ft.llmCall.provider === 'openai'
                                      ? ADMIN_BADGE_INFO_CLASSNAME
                                      : ADMIN_BADGE_WARNING_CLASSNAME
                                  }
                                >
                                  {ft.llmCall.provider}
                                </span>
                                <span className={ADMIN_MUTED_TEXT_CLASSNAME}>
                                  {ft.llmCall.model.length > 20 ? ft.llmCall.model.substring(0, 20) + '...' : ft.llmCall.model}
                                </span>
                                {ft.llmCall.costUsd !== null && (
                                  <span className="text-xs text-emerald-600 dark:text-emerald-400">
                                    ${ft.llmCall.costUsd.toFixed(6)}
                                  </span>
                                )}
                              </div>
                            </button>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="admin-table-cell-muted">
                          {new Date(ft.createdAt).toLocaleString('es-ES', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
        </AdminTableWrap>
          <AdminPaginationBar
            page={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalCount={totalCount}
          />
      </div>

      {selectedLLMCall && (
        <div
          className={ADMIN_MODAL_OVERLAY_CLASSNAME}
          onClick={() => setSelectedLLMCall(null)}
        >
          <div
            className={ADMIN_MODAL_CLASSNAME}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6 flex items-center justify-between">
              <h2 className="m-0 text-lg font-semibold text-foreground">{t('llm_call_details') || 'Detalles de la Llamada LLM'}</h2>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSelectedLLMCall(null)}
              >
                ×
              </Button>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>{t('date')}</div>
              <div className={ADMIN_BODY_TEXT_CLASSNAME}>
                {new Date(selectedLLMCall.createdAt).toLocaleString('es-ES')}
              </div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>{t('trigger_source') || 'Origen'}</div>
              <div className={ADMIN_BODY_TEXT_CLASSNAME}>{selectedLLMCall.triggerSource}</div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>{t('provider_model') || 'Proveedor / Modelo'}</div>
              <div className={ADMIN_BODY_TEXT_CLASSNAME}>
                {selectedLLMCall.provider} / {selectedLLMCall.model}
              </div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>{t('tokens_label')}</div>
              <div className={ADMIN_BODY_TEXT_CLASSNAME}>
                {t('prompt_tokens')}: {selectedLLMCall.promptTokens?.toLocaleString() || 'N/A'} | {t('response')}:{' '}
                {selectedLLMCall.responseTokens?.toLocaleString() || 'N/A'} | {t('total')}:{' '}
                {((selectedLLMCall.promptTokens || 0) + (selectedLLMCall.responseTokens || 0)).toLocaleString()}
              </div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>{t('duration')}</div>
              <div className={ADMIN_BODY_TEXT_CLASSNAME}>
                {selectedLLMCall.durationSeconds !== null ? `${selectedLLMCall.durationSeconds.toFixed(2)} ${t('seconds')}` : 'N/A'}
              </div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>{t('cost')}</div>
              <div className="text-base font-medium text-emerald-600 dark:text-emerald-400">
                {selectedLLMCall.costUsd !== null ? `$${selectedLLMCall.costUsd.toFixed(6)}` : 'N/A'}
              </div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>{t('status')}</div>
              <div className={`${ADMIN_BODY_TEXT_CLASSNAME} flex items-center gap-2`}>
                {selectedLLMCall.response === null ? (
                  <>
                    <span>⏳</span>
                    <span>{t('pending') || 'Pendiente'}</span>
                  </>
                ) : selectedLLMCall.response.startsWith('Error:') ? (
                  <>
                    <span>❌</span>
                    <span className="text-destructive">Error</span>
                  </>
                ) : (
                  <>
                    <span>✅</span>
                    <span className="text-emerald-600 dark:text-emerald-400">{t('success') || 'Éxito'}</span>
                  </>
                )}
              </div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>Prompt</div>
              <div className="max-h-[200px] overflow-auto whitespace-pre-wrap rounded-md border border-border bg-muted/40 p-4 font-mono text-sm text-foreground">
                {selectedLLMCall.prompt}
              </div>
            </div>

            <div className="mb-6">
              <div className={`${ADMIN_MUTED_TEXT_CLASSNAME} mb-2`}>{t('response') || 'Respuesta'}</div>
              <div className="max-h-[400px] overflow-auto whitespace-pre-wrap rounded-md border border-border bg-muted/40 p-4 font-mono text-sm text-foreground">
                {selectedLLMCall.response || (t('no_response') || 'Sin respuesta')}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

